import { For, Show, createResource, createSignal, onCleanup, onMount } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import { useApi, isOk } from '../api';
import type { SelfShutdownIncidentDto } from '../api';
import Text from './Text';
import { useTranslation } from '../hooks/useTranslation';
import { useToast } from './MessageBox';
import { formatDateTime } from '../services/dateFormat';
import { selfShutoffAcknowledgedRevision, notifySelfShutoffAcknowledged, selfShutoffTriggerKey } from '../services/selfShutoff';
import styles from './SelfShutoffBanner.module.css';

const POLL_MS = 30_000;

export default function SelfShutoffBanner() {
    const api = useApi();
    const navigate = useNavigate();
    const toast = useToast();
    const { t } = useTranslation();

    const [reloadToken, setReloadToken] = createSignal(0);
    const [acknowledging, setAcknowledging] = createSignal(false);
    const seenIds = new Set<string>();

    const [incidents, { refetch }] = createResource(
        () => ({ reload: reloadToken(), acknowledged: selfShutoffAcknowledgedRevision() }),
        async (): Promise<SelfShutdownIncidentDto[]> => {
            try {
                const res = await api.botSelfShutdown.listOpen();
                if (!isOk(res.data)) return [];
                const open = res.data.value.incidents ?? [];

                // Toast only what appeared since the last poll, so a standing incident does not
                // re-toast every 30s.
                for (const incident of open) {
                    if (incident.id && !seenIds.has(incident.id)) {
                        seenIds.add(incident.id);
                        toast?.error(t('SELF_SHUTOFF_TOAST', incident.botUsername ?? '?'));
                    }
                }
                return open;
            } catch {
                return [];
            }
        }
    );

    onMount(() => {
        const handle = window.setInterval(() => {
            if (document.visibilityState === 'visible') void refetch();
        }, POLL_MS);
        onCleanup(() => window.clearInterval(handle));
    });

    const triggerLabel = (trigger: number | undefined) =>
        t(selfShutoffTriggerKey(trigger));

    const acknowledgeAll = async () => {
        const open = incidents() ?? [];
        if (open.length === 0) return;
        setAcknowledging(true);
        try {
            for (const incident of open) {
                if (incident.id) await api.botSelfShutdown.acknowledge(incident.id);
            }
            notifySelfShutoffAcknowledged();
            setReloadToken((n) => n + 1);
        } catch {
            toast?.error(t('SELF_SHUTOFF_ACK_FAILED'));
        } finally {
            setAcknowledging(false);
        }
    };

    return (
        <Show when={(incidents() ?? []).length > 0}>
            <div class={styles.banner} role="alert">
                <i class={`fa-solid fa-gavel ${styles.icon}`} />
                <div class={styles.text}>
                    <span class={styles.title}>
                        <Text val="SELF_SHUTOFF_BANNER_TITLE" />
                    </span>
                    <ul class={styles.list}>
                        <For each={incidents()}>{(incident) => (
                            <li class={styles.row}>
                                <span class={styles.botName}>{incident.botUsername}</span>
                                <span>{triggerLabel(incident.trigger)}</span>
                                <span class={styles.when}>{formatDateTime(incident.occurredAtUtc)}</span>
                            </li>
                        )}</For>
                    </ul>
                </div>
                <div class={styles.actions}>
                    <button
                        class="btn btn-secondary btn-sm"
                        onClick={() => navigate(`/selfshutoff/${(incidents() ?? [])[0]?.id}`)}
                    >
                        <Text val="SELF_SHUTOFF_DETAILS" />
                    </button>
                    <button
                        class="btn btn-primary btn-sm"
                        disabled={acknowledging()}
                        onClick={() => void acknowledgeAll()}
                    >
                        <Show when={acknowledging()} fallback={<Text val="SELF_SHUTOFF_ACKNOWLEDGE" />}>
                            <i class="fa-solid fa-spinner fa-spin" />
                        </Show>
                    </button>
                </div>
            </div>
        </Show>
    );
}
